import React, { ReactNode, useState, useEffect } from 'react';
import { AddCircleOutlineOutlined, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import { DocumentSearchReplaceSuggestion } from 'shared/models/api/models';
import * as api from 'shared/services/railDocumentsApi';
import { Toolbar, IActionProps } from 'components/ui/Toolbar';
import { DataGrid, IDataCol } from 'components/ui/DataGrid';
import { FormModal, FormModalPaddedBody, FormModalSubHeader, ModalButton, ModalButtonRow } from 'components/ui/FormModal';
import InfoModal, { ExitCancelModel } from 'components/ui/InfoModal';
import { FormFeedback, Input } from 'reactstrap';
import { stringSort } from 'helpers/HelperFunctions';

const addNewActionButton = {
    name: "Add new",
    icon: AddCircleOutlineOutlined,
    permission: "web.admin.general.acronyms.read"
} as IActionProps;

const editActionButton = {
    name: "View/Edit",
    icon: EditOutlined,
    permission: "web.admin.general.acronyms.read",
    minSelectedCount: 1,
    maxSelectedCount: 1
} as IActionProps;

const deleteActionButton = {
    name: "Delete",
    icon: DeleteOutlined,
    permission: "web.admin.general.acronyms.read",
    minSelectedCount: 1
} as IActionProps;

const toolbarActions = [addNewActionButton, editActionButton, deleteActionButton];

const expressionColumn = {
    name: "expression",
    heading: "Expression",
    className: "Expression",
    sort: (a: DocumentSearchReplaceSuggestion, b: DocumentSearchReplaceSuggestion, order: number) =>
        (stringSort(a.expression, b.expression, true) * order)
} as IDataCol;

const suggestionColumn = {
    name: "suggestion",
    heading: "Suggestion",
    className: "Suggestion",
    sort: (a: DocumentSearchReplaceSuggestion, b: DocumentSearchReplaceSuggestion, order: number) =>
        (stringSort(a.suggestion, b.suggestion, true) * order)
} as IDataCol;

const gridColumns = [expressionColumn, suggestionColumn];

interface IMessage {
    text: string;
}

interface IPrompt {
    text: string;
    okAction: () => void;
}

interface IForm {
    acronym: DocumentSearchReplaceSuggestion
    title: string;
    icon: any;
    closeAction: () => void;
    okAction: (acronym: DocumentSearchReplaceSuggestion) => void;
}

const AcronymForm = (props: IForm) => {
    const [expression, setExpression] = useState('');
    const [suggestion, setSuggestion] = useState('');

    const [expressionInvalid, setExpressionInvalid] = useState(false);
    const [suggestionInvalid, setSuggestionInvalid] = useState(false);

    const minLength = 2;
    const maxLength = 50;

    const formValid = () => {
        let valid = true;

        if (expression.length < 2 || expression.length > 100) {
            setExpressionInvalid(true);
            valid = false;
        }
        else {
            setExpressionInvalid(false);
        }

        if (suggestion.length < minLength || suggestion.length > maxLength) {
            setSuggestionInvalid(true);
            valid = false;
        }
        else {
            setSuggestionInvalid(false);
        }

        return valid;
    }

    const save = () => {

        if (!formValid()) {
            return;
        }

        let saveAcronym = { ...props.acronym };

        saveAcronym.expression = expression;
        saveAcronym.suggestion = suggestion;

        props.okAction(saveAcronym);
    }

    useEffect(() => {
        setExpression(props.acronym?.expression || '');
        setSuggestion(props.acronym?.suggestion || '');
        setExpressionInvalid(false);
        setSuggestionInvalid(false);
    }, [props.acronym]);

    return (
        <FormModal
            isOpen={!!props.acronym}
            icon={props.icon}
            closeAction={props.closeAction}
            headingText={props.title || ''}>
            <>
                <FormModalSubHeader>
                    <div>Acronym Details</div>
                </FormModalSubHeader>
                <FormModalPaddedBody>
                    <div className="modal-body-content-container">

                        <div className="modal-body-content acronym-form">
                            <div className="row">
                                <div className="col-12 col-md-3">Expression:</div>
                                <div className="col-12 col-md-9">
                                    <Input
                                        type="text"
                                        value={expression}
                                        maxLength={maxLength}
                                        onChange={(e) => setExpression(e.target.value)}
                                        invalid={expressionInvalid}
                                        required={true}
                                    />
                                    <FormFeedback>Minimum length: {minLength}</FormFeedback>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-3">Suggestion:</div>
                                <div className="col-12 col-md-9">
                                    <Input
                                        type="text"
                                        value={suggestion}
                                        maxLength={maxLength}
                                        onChange={(e) => setSuggestion(e.target.value)}
                                        invalid={suggestionInvalid}
                                        required={true}
                                    />
                                    <FormFeedback>Minimum length: {minLength}</FormFeedback>
                                </div>
                            </div>
                        </div>

                        <ModalButtonRow>
                            <ModalButton onClick={props.closeAction} text="Cancel" />
                            <ModalButton onClick={save} text="Submit" primaryButton={true} />
                        </ModalButtonRow>

                    </div>
                </FormModalPaddedBody>
            </>
        </FormModal>
    );
}

const AcronymsPage = () => {
    const [acronyms, setAcronyms] = useState([] as DocumentSearchReplaceSuggestion[]);
    const [selectedAcronyms, setSelectedAcronyms] = useState([] as DocumentSearchReplaceSuggestion[]);
    const [refresh, setRefresh] = useState({});
    const [message, setMessage] = useState(undefined as IMessage | undefined);
    const [prompt, setPrompt] = useState(undefined as IPrompt | undefined);
    const [form, setForm] = useState(undefined as IForm | undefined);

    const openMessage = (msg: string) => {
        setMessage({ text: msg });
    }

    const closeMessage = () => {
        setMessage(undefined);
    }

    const openPrompt = (msg: string, okAction: () => void) => {
        setPrompt({ text: msg, okAction: okAction });
    }

    const closePrompt = () => {
        setPrompt(undefined);
    }

    const newForm = () => {
        setSelectedAcronyms([]);
        setForm({
            acronym: { id: 0, expression: '', suggestion: '' },
            title: "Add New Acronym",
            icon: <AddCircleOutlineOutlined />,
            closeAction: closeForm,
            okAction: (acronym) => addAcronym(acronym)
        });
    }

    const editForm = (acronym: DocumentSearchReplaceSuggestion) => {
        setForm({
            acronym: acronym,
            title: "Edit Acronym",
            icon: <EditOutlined />,
            closeAction: closeForm,
            okAction: (acronym) => editAcronym(acronym)
        });
    }

    const closeForm = () => {
        setForm(undefined);
    }

    const checkMinMax = (action: IActionProps) => {
        if (selectedAcronyms.length === 0 && (action.minSelectedCount ?? 0) > 0) {
            openMessage("Please select one item to continue.");
            return false;
        }

        if (!action.maxSelectedCount) {
            return true;
        }

        if (selectedAcronyms.length > action.maxSelectedCount) {
            if (action.maxSelectedCount === 1) {
                openMessage("Please select only one item to continue.");
            }
            else {
                openMessage("Please select " + action.maxSelectedCount + " or fewer items to continue.");
            }
            return false;
        }

        return true;
    };

    const addAcronym = async (acronym: DocumentSearchReplaceSuggestion) => {
        var result = await api.addSearchReplaceSuggestion(acronym);
        if (result) {
            openMessage(`Acronym added`);
            setRefresh({});
            closeForm();
        }
        else {
            openMessage(`Error saving acronym`);
        }
    }

    const editAcronym = async (acronym: DocumentSearchReplaceSuggestion) => {
        var result = await api.editSearchReplaceSuggestion(acronym);
        if (result) {
            openMessage(`Acronym updated`);
            setRefresh({});
            closeForm();
        }
        else {
            openMessage(`Error saving acronym`);
        }
    }

    const deleteSelectedAcronyms = async () => {
        closePrompt();
        const deleteIds = selectedAcronyms.map(a => a.id);
        var success = await api.deleteSearchReplaceSuggestion(deleteIds);
        setRefresh({});
        if (success) {
            openMessage("Selected items deleted");
        }
        else {
            openMessage("Error deleting items");
        }
    }

    const actionClicked = (action: IActionProps) => {
        if (!checkMinMax(action)) {
            return;
        }

        switch (action) {
            case addNewActionButton:
                newForm();
                return;
            case editActionButton:
                editForm(selectedAcronyms[0]);
                return;
            case deleteActionButton:
                openPrompt('Are you sure you want to delete the selected acronyms?', deleteSelectedAcronyms);
                return;
            default:
                console.warn("Unhandled action: " + action.name);
        }
    }

    const onGridRowDoubleClick = (acronym: DocumentSearchReplaceSuggestion) => {
        editAcronym(acronym);
    }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await api.documentSearchReplaceSuggestions();

            if (mounted) {
                setAcronyms(results);
                setSelectedAcronyms([]);
            }
        };

        mount();
        return () => { mounted = false };
    }, [refresh]);

    return (
        <>

            <InfoModal
                text={message?.text || ''}
                isOpen={!!message}
                closeAction={closeMessage} />

            <ExitCancelModel
                text={prompt?.text || ''}
                isOpen={!!prompt}
                actionButtonText={'OK'}
                action={prompt?.okAction || (() => { })}
                closeAction={closePrompt}
                closeButtonText={'Cancel'} />

            <AcronymForm {...form || {} as IForm} />

            <Toolbar actions={toolbarActions}
                onClick={actionClicked}
                selectedCount={selectedAcronyms.length} />

            <DataGrid
                rows={acronyms}
                selectedItems={selectedAcronyms}
                setSelectedItems={setSelectedAcronyms}
                selectMode={'many'}
                pageSize={9500}
                columns={gridColumns}
                onDoubleClick={onGridRowDoubleClick} />
        </>
    );
};

export default AcronymsPage;
